import { useReducer, useMemo } from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import {
  timeInputReducer,
  initialReducerState,
} from '../SmartTimeInput.reducer'
import type { State } from '../SmartTimeInput.reducer'
import { extractPlainTime } from '../utils/temporalHelpers'

export type SmartTimeInputActions = {
  setInput: (value: string, resetNavigation?: boolean) => void
  openMenu: () => void
  closeMenu: () => void
  setActiveIndex: (
    index: number,
    userInitiated?: boolean,
    shouldScroll?: boolean,
  ) => void
  navigate: (direction: 'up' | 'down', length: number) => void
  setFocus: (isFocused: boolean) => void
  setInternalValue: (value: Temporal.PlainTime | null) => void
  setUserNavigated: (userNavigated: boolean) => void
  autoHighlight: (index: number) => void
  resetActiveIndex: () => void
}

export type UseSmartTimeInputStateReturn = {
  state: State
  actions: SmartTimeInputActions
}

export const useSmartTimeInputState = (
  defaultValue: Temporal.PlainTime | Temporal.ZonedDateTime | null = null,
): UseSmartTimeInputStateReturn => {
  const [state, dispatch] = useReducer(
    timeInputReducer,
    initialReducerState,
    (base) => ({
      ...base,
      internalValue: defaultValue ? extractPlainTime(defaultValue) : null,
    }),
  )

  const actions: SmartTimeInputActions = useMemo(
    () => ({
      setInput: (value: string, resetNavigation?: boolean) => {
        dispatch({ type: 'SET_INPUT', value, resetNavigation })
      },

      openMenu: () => {
        dispatch({ type: 'OPEN_MENU' })
      },

      closeMenu: () => {
        dispatch({ type: 'CLOSE_MENU' })
      },

      setActiveIndex: (
        index: number,
        userInitiated?: boolean,
        shouldScroll?: boolean,
      ) => {
        dispatch({
          type: 'SET_ACTIVE_INDEX',
          index,
          userInitiated,
          shouldScroll,
        })
      },

      navigate: (direction: 'up' | 'down', length: number) => {
        dispatch({ type: 'NAVIGATE', direction, length })
      },

      setFocus: (isFocused: boolean) => {
        dispatch({ type: 'SET_FOCUS', isFocused })
      },

      setInternalValue: (value: Temporal.PlainTime | null) => {
        dispatch({ type: 'SET_INTERNAL_VALUE', value })
      },

      setUserNavigated: (userNavigated: boolean) => {
        dispatch({ type: 'SET_USER_NAVIGATED', userNavigated })
      },

      autoHighlight: (index: number) => {
        dispatch({ type: 'AUTO_HIGHLIGHT', index })
      },

      resetActiveIndex: () => {
        dispatch({ type: 'RESET_ACTIVE_INDEX' })
      },
    }),
    [],
  )

  return { state, actions }
}
